import PlaylistCard from "../../components/cards/PlaylistCard";
import { useTrackOnMount } from "../../hooks/use-track-on-mount";
import { useLoadedAudioId } from "../../domains/app/contexts/audio.context";
import { useArticleSwipeCache } from "../../features/article/stores/useArticleSwipeCache";
import {
  formatArticleDate,
  formatShortDate,
} from "../../domains/app/utils/renderDate";
import { exploreOriginState } from "../../domains/article/utils/exploreOrigin";
import { gaEventRoomRecommendationShown } from "../../tools/analytics/domains/room.analytics";
import { useHistory } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { IconArrowUpRight, IconBook2 } from "@tabler/icons-react";
import { isDesktopLayout } from "../../common/hooks/useIsDesktopView";
import { Tag } from "../../components/ui/Tag";
import SkeletonPublicationCard from "../../components/skeleton/SkeletonPublicationCard";
import Skeleton from "../../components/skeleton/Skeleton";
import { PublicationCard } from "../../components/ui/PublicationCard";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { SectionLink } from "../../components/ui/SectionLink";
import { type PracticeFeedArticle } from "../../domains/practice/hooks/usePracticeFeed";
import {
  type PracticeRailCorpus,
  type PracticeRailTerm,
} from "../../domains/practice/hooks/usePracticeRailTerms";
import {
  practiceRailCorpusKey,
  recordPracticeRailArticleClicked,
  practiceRailProfileUid,
  recordPracticeRailViewed,
} from "../../domains/practice/practiceRailEvents";
import { openSettingsAt } from "../../domains/app/stores/settingsSheetStore";
import { openDesktopSettings } from "../../domains/app/stores/desktopSettingsStore";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import type { ForYouFeedItem } from "../../domains/forYou/hooks/useGetForYouRecommendedContentQuery";
import { useViewportImpression } from "../../domains/forYou/impressions/useViewportImpression";
import type { ImpressionPayload } from "../../domains/forYou/impressions/impressionBatcher";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import { getArticleTypeVariant } from "../../domains/app/utils/getArticleTypeText";
import { getLastRoomVisit } from "../../domains/room/utils/roomVisits";
import { hasReadArticle } from "../../domains/article/utils/articleReads";
import { usePlaylistPanelContent } from "../../domains/playlist/hooks/usePlaylistPanelContent";
import {
  useGetRecommendedPlaylistsQuery,
  seedPlaylistDetailCache,
  type ApiPlaylistModel,
} from "../../domains/playlist/hooks/useGetRecommendedPlaylistsQuery";
import { useSaveArticle } from "../../domains/user/hooks/useSaveArticle";
import { seedArticleDetailCache } from "../../features/article/hooks/useArticleData";
import { useShareArticle } from "../../domains/user/hooks/useShareArticle";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import {
  gaEventViewItemListPlaylist,
  gaEventSelectItemPlaylist,
} from "../../tools/analytics/domains/playlist.analytics";
import PlaylistPanel from "../../components/app/PlaylistPanel";
import { DesktopCollectionsBand } from "../../components/discovery/DesktopCollectionsBand";
import { SaveableCollectionCard } from "../../domains/playlist/components/SaveableCollectionCard";
import { DesktopArticleRow } from "../../components/discovery/DesktopArticleRow";
import { DesktopSaveButton } from "../../components/save/DesktopSaveButton";
import { authorLine } from "../../domains/article/utils/authorLine";

type FeedArticleItem = Extract<ForYouFeedItem, { content_format: "Article" }>;

/**
 * A card's chip is the BEST-RANKED rail term the publication carries. Rail order
 * is score order, so the first match is the best one — and that is what keeps the
 * invariant: a card chip is ALWAYS a chip of the rail, never a dead end.
 *
 * It therefore can name something other than the filter in force. In the
 * "Robotic" thread an article that also carries a better-ranked term shows that
 * one, tappable: a lateral move, never a cul-de-sac. The alternative (always
 * showing the thread's own term) makes every chip identical and inert.
 *
 * `null` when the article carries no rail term at all, which an EMPTY rail makes
 * true of every card: a chip that is not in the rail could not be selected, so
 * there is no fallback to draw.
 */
export function railTermForArticle(
  terms: ReadonlyArray<PracticeRailTerm>,
  carriedByArticleId: ReadonlyMap<string, readonly string[]>,
  articleId: string
): PracticeRailTerm | null {
  const carried = carriedByArticleId.get(articleId);
  // The endpoint serves one entry per doc, always — so a miss is a doc the feed
  // is rendering from a page whose sidecar it does not hold, which cannot happen
  // while both come out of the same response. Empty reads as "carries none".
  if (carried === undefined) return null;
  const best = terms.find((term) => carried.includes(term.descriptorUi));
  return best === undefined ? null : best;
}

/**
 * The loading silhouette, and why it is not `SkeletonPublicationCard` bare: the
 * real card now has a journal row above it carrying the chip, so the shared
 * skeleton is one row short and the whole feed jumps by that row's height the
 * moment real cards arrive. Reserved here rather than inside
 * `SkeletonPublicationCard`, for the same reason the row itself is owned here —
 * that component is shared with every other surface.
 */
export function PracticeCardSkeleton({
  /**
   * `WEBAPP_PRACTICE_CARD_MESH_CHIP`, drilled rather than read again here.
   *
   * The chip's silhouette has to go with the chip: reserving it under a flag that
   * is off draws a grey box that never becomes anything, which is the one thing a
   * skeleton must not do. The journal's silhouette stays either way, because the
   * ROW stays either way and it is what keeps the feed from jumping.
   *
   * Required, not defaulted: this prop exists to be answered at every call site,
   * and a default would let a new one silently promise a chip.
   */
  showChip,
}: {
  showChip: boolean;
}): React.JSX.Element {
  return (
    <div className="flex w-full flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <Skeleton className="rounded-lg" width={132} height={24} />
        {showChip && <Skeleton className="rounded" width={88} height={20} />}
      </div>
      <SkeletonPublicationCard showImage showDescription />
    </div>
  );
}

/**
 * Mint plus an outgoing arrow when tapping it takes you somewhere, inert slate
 * when the feed is already in that thread. One glyph carries the difference.
 */
function MeshCardChip({
  term,
  inThread,
  onSelect,
}: {
  term: PracticeRailTerm;
  inThread: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  if (inThread)
    return (
      <Tag
        size="sm"
        variant="slate"
        state="idle"
        icon={null}
        className="max-w-40 shrink-0"
      >
        <span className="min-w-0 truncate">{term.label}</span>
      </Tag>
    );
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onSelect();
      }}
      className="max-w-40 shrink-0 cursor-pointer"
    >
      <Tag
        size="sm"
        variant="mint"
        state="idle"
        icon={<IconArrowUpRight size={12} stroke={2} className="shrink-0" />}
        className="max-w-full"
      >
        <span className="min-w-0 truncate">{term.label}</span>
      </Tag>
    </button>
  );
}

/**
 * The journal line, owned HERE rather than by `PublicationCard`.
 *
 * The design puts the MeSH chip on the journal's row, right-aligned, and the card
 * has no slot for a trailing element. Adding one (`meshChip`, or a general
 * `journalTrailing`) is the ONE production-component change this design really
 * wants, and it is deliberately not made in this lot: `PublicationCard` is shared
 * with every other surface, and this page has to stay deletable as a file. So the
 * row lives here and the card below gets `journalName={null}`.
 *
 * The journal stays tappable, which the prototype's version was not — dropping
 * navigation the control page has would be a regression, not a simplification.
 */
function PracticeCardJournalRow({
  journalName,
  onJournalClick,
  chip,
}: {
  journalName: string | null;
  onJournalClick: (() => void) | null;
  chip: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex items-center justify-between gap-2">
      {journalName === null ? (
        <span />
      ) : (
        <button
          type="button"
          disabled={onJournalClick === null}
          onClick={(event) => {
            event.stopPropagation();
            if (onJournalClick !== null) onJournalClick();
          }}
          className="flex min-w-0 items-center gap-1 text-left text-footnote"
        >
          <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-lg text-ds-slate-600">
            <IconBook2 stroke={1.5} className="size-4" />
          </span>
          <span className="min-w-0 truncate font-normal text-ds-slate-600">
            <Trans
              i18nKey="In <name>{{name}}</name>"
              values={{ name: journalName }}
              components={{
                name: <span className="font-semibold text-ds-slate-700" />,
              }}
            />
          </span>
        </button>
      )}
      {chip}
    </div>
  );
}

/**
 * One card of the practice feed.
 *
 * Save and share are live — this surface has no signed-out reader, since
 * `DiscoverySurface` serves those the control page. `recommendation` and `isNew`
 * are `null`/`false` by construction, not by omission: the first is a ranked
 * feed's "From {Room}" eyebrow, the second is defined relative to a room visit,
 * and neither means anything about a set a MeSH Boolean matched.
 */
export function PracticeArticleFeedItem({
  item,
  corpus,
  layout,
  openedFrom,
}: {
  item: PracticeFeedArticle;
  /**
   * The list the article opens from and its place in it, handed to the
   * article swipe cache on open: the desktop audio player continues into the
   * next one (Up next). Null keeps the cache as it is (the phone).
   */
  openedFrom: { articles: PracticeFeedArticle[]; index: number } | null;
  /**
   * Which corpus served this card. Carried down so the open is attributable to
   * ONE practice: without it the rail's engagement is a single number for a
   * reader holding up to six, and a thin practice cannot be told from a good one.
   */
  corpus: PracticeRailCorpus;
  /**
   * `card` is the mobile card, which opens the article page and names a rail
   * term (`chip`, already resolved by the page). `desktop` draws a
   * `DesktopArticleRow`, which carries no chip. Both open the article page.
   */
  layout:
    | {
        kind: "card";
        chip: PracticeRailTerm | null;
        selectedDescriptorUi: string | null;
        onSelectDescriptor: (descriptorUi: string) => void;
      }
    | {
        kind: "desktop";
        size: "row" | "hero";
      };
}) {
  const loadedAudioId = useLoadedAudioId();
  const history = useHistory();
  // The same viewport impression the ranked feed reports, on its OWN surface.
  // Without it this arm reports nothing at all, and the recommender's 30-day
  // anti-repetition window goes dark for the whole cohort — including the
  // control feed they land back on when the flag is switched off.
  const impressionRef = useViewportImpression({
    contentKind: "article",
    contentId: item.id,
    surface: "practice",
    // The corpus the prop above was threaded down for. Without it the row is
    // written null, which on this surface is not "unknown" but a claim: the
    // reader was on the "All practices" aggregate. Every per-practice
    // impression denominator read the aggregate's until this was passed.
    practiceProfileUid: practiceRailProfileUid(corpus),
  });
  const { articleLink } = usePageLinks(null);
  const { currentUser } = useCurrentUser();
  const { activeLang } = useActiveLang();
  const playlistPanel = usePlaylistPanelContent();
  const { setDocs: setSwipeList, setIndex: setSwipeIndex } =
    useArticleSwipeCache();
  const { isSaved, toggleSaveToPlaylists } = useSaveArticle(
    item,
    AnalyticsItemList.PRACTICE_FEED
  );
  const { triggerShare } = useShareArticle(AnalyticsItemList.PRACTICE_FEED);

  const journal = item.journal;
  const cardImage = item.cardImage;
  const imageUrl =
    cardImage === null
      ? null
      : cardImage.sizes === null
        ? cardImage.url
        : cardImage.sizes.medium.file;
  const meta = item.article_type === null ? "Article" : item.article_type;
  const description =
    item.tldr !== null && item.tldr.length > 0 ? item.tldr : null;

  const onJournalClick =
    journal === null
      ? null
      : () => {
          history.push(`/journal/${journal.uid}`);
        };

  const onBookmark = () => {
    void playlistPanel.openArticle(item.slug);
  };
  const onShare =
    currentUser === null
      ? null
      : () => {
          void triggerShare(item, currentUser);
        };
  const openArticle = () => {
    // Before the navigation, and NOT the destination page's `view_item`:
    // that one is stamped `article_page`, so its reverse-ETL files every
    // article open in the product under `feed` and can never say that
    // THIS arm's readers opened anything.
    recordPracticeRailArticleClicked(corpus, item.id);
    seedArticleDetailCache(item, null);
    if (openedFrom !== null) {
      setSwipeList(openedFrom.articles);
      setSwipeIndex(openedFrom.index);
    }
    history.push(
      articleLink(item),
      exploreOriginState(history.location.search, openedFrom !== null)
    );
  };

  const content =
    layout.kind === "desktop" ? (
      <DesktopArticleRow
        onRemove={null}
        isInPlayer={loadedAudioId === item.id}
        layout={layout.size}
        title={item.title}
        author={authorLine(item.primary_author, item.authors)}
        description={description}
        date={formatArticleDate(item.publication_date, activeLang)}
        readingMinutes={null}
        meta={meta}
        metaVariant={getArticleTypeVariant(item.article_type)}
        access={item.isOpenAccess === true ? "open" : "closed"}
        imageUrl={imageUrl}
        bookmark={
          <DesktopSaveButton
            article={item}
            analyticsListName={AnalyticsItemList.PRACTICE_FEED}
            size="row"
          />
        }
        onShare={onShare}
        onClick={openArticle}
        source={
          journal === null
            ? null
            : {
                kind: "journal",
                name: journal.name,
                impactFactor:
                  journal.impact_factor === 0 ? null : journal.impact_factor,
                onClick: onJournalClick,
              }
        }
      />
    ) : (
      <>
        <PracticeCardJournalRow
          journalName={journal === null ? null : journal.name}
          onJournalClick={onJournalClick}
          chip={
            layout.chip === null ? null : (
              <MeshCardChip
                term={layout.chip}
                inThread={
                  layout.chip.descriptorUi === layout.selectedDescriptorUi
                }
                onSelect={() => {
                  if (layout.chip === null)
                    throw new Error("should not happen: the chip is drawn");
                  layout.onSelectDescriptor(layout.chip.descriptorUi);
                }}
              />
            )
          }
        />
        <PublicationCard
          isInPlayer={loadedAudioId === item.id}
          title={item.title}
          // The journal moved to the row above, which owns the MeSH chip beside it.
          journalName={null}
          author={authorLine(item.primary_author, item.authors)}
          date={formatArticleDate(item.publication_date, activeLang)}
          meta={meta}
          metaVariant={getArticleTypeVariant(item.article_type)}
          access={item.isOpenAccess === true ? "open" : "closed"}
          recommendation={null}
          isNew={false}
          isBookmarked={isSaved}
          onBookmark={onBookmark}
          onShare={onShare}
          onClick={openArticle}
          onJournalClick={null}
          onRecommendationClick={null}
          imageUrl={imageUrl}
          description={description}
        />
      </>
    );

  return (
    <div ref={impressionRef} className="flex w-full flex-col gap-1">
      {content}
      {playlistPanel.state.status === "ready" && (
        <PlaylistPanel
          isVisible
          contentToAdd={playlistPanel.state.content}
          onClose={playlistPanel.close}
          onSave={async ({
            add,
            remove,
          }: {
            add: string[];
            remove: string[];
          }) => {
            await toggleSaveToPlaylists(item, { add, remove });
          }}
        />
      )}
    </div>
  );
}

/**
 * What the practice feed says when it holds nothing.
 *
 * ONE wording, because the feed asks one question: this practice, the one whose
 * chip is selected. It used to have a second for the aggregate chip ("your
 * practice"), and both that chip and its endpoint are gone.
 *
 * There is no wording for "has declared no practice at all". That state cannot
 * reach this page: `DiscoverySurface` requires a non-empty catalogue to mount it,
 * so a reader with nothing gets the ordinary discovery surface rather than a feed
 * apologising for a question nobody asked them.
 */
export function PracticeFeedEmpty(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-2 py-6">
      <p className="m-0 text-center text-footnote text-ds-slate-500">
        {t("We hold no publication for this practice yet.")}
      </p>
      <button
        type="button"
        className="cursor-pointer border-none bg-transparent p-0 text-footnote text-ds-slate-500 underline"
        onClick={() => {
          // A desktop reader edits the practices in the settings modal, over
          // this feed; the bottom sheet is the mobile answer.
          if (isDesktopLayout()) {
            openDesktopSettings({ page: "practice" });
            return;
          }
          openSettingsAt({ view: "practice" });
        }}
      >
        {t("Edit your practice")}
      </button>
    </div>
  );
}

/**
 * The feed could not be evaluated. Distinct from `PracticeFeedEmpty` on purpose:
 * one is a statement about the corpus, this one is a statement about us, and the
 * reader can only act on the second. No detail from the response is shown — a
 * status code says nothing a practitioner can use — and no retry button, because
 * the query refetches on focus and on reconnect already.
 */
export function PracticeFeedFailed(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <p className="m-0 py-6 text-center text-footnote text-ds-slate-500">
      {t("We couldn't load your practice feed. Please try again in a moment.")}
    </p>
  );
}

/** How a feed item draws: the mobile card, or a desktop row. */
type FeedItemLayout = { kind: "card" } | { kind: "desktop" };

function ArticleFeedItem({
  item,
  roomId,
  isNew,
  layout,
}: {
  item: FeedArticleItem;
  roomId: string | null;
  isNew: boolean;
  layout: FeedItemLayout;
}) {
  const loadedAudioId = useLoadedAudioId();
  const history = useHistory();
  const { articleLink } = usePageLinks(roomId);
  const { currentUser } = useCurrentUser();
  const { activeLang } = useActiveLang();
  const playlistPanel = usePlaylistPanelContent();
  const { isSaved, toggleSaveToPlaylists } = useSaveArticle(
    item,
    AnalyticsItemList.FORYOU
  );
  const { triggerShare } = useShareArticle(AnalyticsItemList.FORYOU);

  const journal = item.journal;
  const cardImage = item.cardImage;
  const imageUrl =
    cardImage === null
      ? null
      : cardImage.sizes === null
        ? cardImage.url
        : cardImage.sizes.medium.file;
  const meta = item.article_type === null ? "Article" : item.article_type;
  const description =
    item.tldr !== null && item.tldr.length > 0 ? item.tldr : null;
  // The "From {Room}" eyebrow only makes sense in the default "For you" feed.
  // When a room chip is selected (roomId !== null) every card is from it.
  const roomReco =
    roomId === null && item.recommendation.reason === "fromRoom"
      ? item.recommendation
      : null;
  const onJournalClick =
    journal === null
      ? null
      : () => {
          history.push(`/journal/${journal.uid}`);
        };
  const onBookmark = () => {
    void playlistPanel.openArticle(item.slug);
  };
  const onShare =
    currentUser === null
      ? null
      : () => {
          void triggerShare(item, currentUser);
        };
  const openArticle = () => {
    seedArticleDetailCache(item, roomId);
    // This feed leaves the swipe cache alone: the article gets no pager.
    history.push(
      articleLink(item),
      exploreOriginState(history.location.search, false)
    );
  };

  return (
    <div>
      {layout.kind === "desktop" ? (
        <DesktopArticleRow
          onRemove={null}
          isInPlayer={loadedAudioId === item.id}
          layout="row"
          title={item.title}
          author={authorLine(item.primary_author, item.authors)}
          description={description}
          date={formatArticleDate(item.publication_date, activeLang)}
          readingMinutes={null}
          meta={meta}
          metaVariant={getArticleTypeVariant(item.article_type)}
          access={item.isOpenAccess === true ? "open" : "closed"}
          imageUrl={imageUrl}
          bookmark={
            <DesktopSaveButton
              article={item}
              analyticsListName={AnalyticsItemList.FORYOU}
              size="row"
            />
          }
          onShare={onShare}
          onClick={openArticle}
          source={
            journal === null
              ? null
              : {
                  kind: "journal",
                  name: journal.name,
                  impactFactor: null,
                  onClick: onJournalClick,
                }
          }
        />
      ) : (
        <PublicationCard
          isInPlayer={loadedAudioId === item.id}
          title={item.title}
          journalName={journal === null ? null : journal.name}
          author={authorLine(item.primary_author, item.authors)}
          date={formatArticleDate(item.publication_date, activeLang)}
          meta={meta}
          metaVariant={getArticleTypeVariant(item.article_type)}
          access={item.isOpenAccess === true ? "open" : "closed"}
          recommendation={roomReco}
          isNew={isNew}
          isBookmarked={isSaved}
          onBookmark={onBookmark}
          onShare={onShare}
          onClick={openArticle}
          onJournalClick={onJournalClick}
          onRecommendationClick={
            roomReco === null
              ? null
              : () => {
                  history.push(`/room/discovery/${roomReco.roomId}`, {
                    backTo: "/discovery",
                  });
                }
          }
          imageUrl={imageUrl}
          description={description}
        />
      )}
      {playlistPanel.state.status === "ready" && (
        <PlaylistPanel
          isVisible
          contentToAdd={playlistPanel.state.content}
          onClose={playlistPanel.close}
          onSave={async ({
            add,
            remove,
          }: {
            add: string[];
            remove: string[];
          }) => {
            await toggleSaveToPlaylists(item, { add, remove });
          }}
        />
      )}
    </div>
  );
}

export function FeedItem({
  item,
  roomId,
  surface,
  isNew,
  layout,
}: {
  item: ForYouFeedItem;
  roomId: string | null;
  /** Drawn by the Article card only: videos and infographics keep their card. */
  layout: FeedItemLayout;
  /**
   * Never `practice`: this is the Room arm of the practice page, and the
   * practice corpus has its own card (`PracticeArticleFeedItem`) precisely
   * because its impressions must name which practice served them.
   */
  surface: Exclude<ImpressionPayload["surface"], "practice">;
  isNew: boolean;
}) {
  const history = useHistory();
  const { activeLang } = useActiveLang();
  const { videoArticleLink, videoStoryLink, infographicLink } =
    usePageLinks(roomId);
  // Recommender v2 (Phase 0.3): report a viewport "seen" impression once this
  // card has been >=1s in view. Hook called unconditionally; the ref only
  // attaches to rendered cards (Nectar/Audio render null → no impression).
  const impressionRef = useViewportImpression({
    contentKind: item.content_format === "Article" ? "article" : "content",
    contentId: item.id,
    surface,
  });

  const card = (() => {
    switch (item.content_format) {
      case "Article": {
        return (
          <ArticleFeedItem
            item={item}
            roomId={roomId}
            isNew={isNew}
            layout={layout}
          />
        );
      }
      case "Nectar": {
        return null;
      }
      case "Video": {
        const journal = item.journal;
        const roomReco =
          roomId === null && item.recommendation.reason === "fromRoom"
            ? item.recommendation
            : null;
        const link =
          item.videoFormat === "story"
            ? videoStoryLink(item)
            : videoArticleLink(item);
        const commonProps = {
          title: item.title,
          journalName: journal === null ? null : journal.name,
          author: "",
          date: formatShortDate(item.publication_date, activeLang),
          meta: item.content_format,
          metaVariant: "slate" as const,
          access: "open" as const,
          recommendation: roomReco,
          isNew,
          isBookmarked: false,
          onBookmark: null,
          onShare: null,
          onJournalClick:
            journal === null
              ? null
              : () => {
                  history.push(`/journal/${journal.uid}`);
                },
          onRecommendationClick:
            roomReco === null
              ? null
              : () => {
                  history.push(`/room/discovery/${roomReco.roomId}`, {
                    backTo: "/discovery",
                  });
                },
          onClick: () => {
            history.push(link);
          },
        };
        // Videos carry no hero image in the feed.
        return (
          <PublicationCard
            isInPlayer={false}
            {...commonProps}
            imageUrl={null}
            description={null}
          />
        );
      }
      case "Infographic": {
        const journal = item.journal;
        const roomReco =
          roomId === null && item.recommendation.reason === "fromRoom"
            ? item.recommendation
            : null;
        const commonProps = {
          title: item.title,
          journalName: journal === null ? null : journal.name,
          author: "",
          date: formatShortDate(item.publication_date, activeLang),
          meta: item.content_format,
          metaVariant: "slate" as const,
          access: "open" as const,
          recommendation: roomReco,
          isNew,
          isBookmarked: false,
          onBookmark: null,
          onShare: null,
          onJournalClick:
            journal === null
              ? null
              : () => {
                  history.push(`/journal/${journal.uid}`);
                },
          onRecommendationClick:
            roomReco === null
              ? null
              : () => {
                  history.push(`/room/discovery/${roomReco.roomId}`, {
                    backTo: "/discovery",
                  });
                },
          onClick: () => {
            history.push(infographicLink(item));
          },
        };
        // Infographics carry no hero image in the feed.
        return (
          <PublicationCard
            isInPlayer={false}
            {...commonProps}
            imageUrl={null}
            description={null}
          />
        );
      }
      case "Audio": {
        return null;
      }
      default: {
        throw new Error(item satisfies never);
      }
    }
  })();

  if (card === null) return null;
  return <div ref={impressionRef}>{card}</div>;
}

// An item is "New" when it was created after the user last visited one of the
// rooms it belongs to — the same definition the per-room tab badge uses
// (roomHasNewContent), so a tab badge always has matching "New" cards in the
// feed (including the default "For you" feed, which spans several rooms).
export function isItemNew(item: ForYouFeedItem): boolean {
  // Reading the item clears its badge (mirrors the Room pages' new indicator).
  if (hasReadArticle(item.id)) return false;
  const createdAt = new Date(item.meta.creationDate).getTime();
  return item.rooms.some((roomId) => {
    const lastVisit = getLastRoomVisit(roomId);
    return lastVisit !== null && createdAt > lastVisit;
  });
}

// A Room's collections carousel — its editorially-ordered Discovery
// collections, from the public endpoint. Hidden once we know there are none.
//
// There WAS a second source above a practice feed: "Collections for you", the
// caller's MeSH-ranked recommendations. It is gone from this page. Those
// playlists are ranked from the reader's MeSH profile, which is the
// specialty-shaped relevance this whole arm exists to replace — so the rail sat
// above a feed built from practices and answered a different question, in a
// louder voice than the feed's own first cards. The control page keeps it
// (`DiscoveryPage`), which is what makes it a comparison.
//
// A Room is not that. Its collections are chosen by the partner, they are the
// reason a B2B reader taps a Room, and nothing in this arm touches them.
export function RoomCollectionsSection({
  roomId,
  layout,
}: {
  roomId: string;
  /** `desktop` draws the `DesktopCollectionsBand` of the desktop feed. */
  layout: "mobile" | "desktop";
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const { playlists, playlistIds, isLoading } =
    useGetRecommendedPlaylistsQuery(roomId);

  return (
    <CollectionsRail
      key={roomId}
      title={t("Room collections")}
      seeAllTo={`/room/playlists/${roomId}`}
      playlists={playlists}
      playlistIds={playlistIds}
      isLoading={isLoading}
      layout={layout}
    />
  );
}

function CollectionsRail({
  title,
  seeAllTo,
  playlists,
  playlistIds,
  isLoading,
  layout,
}: {
  title: string;
  seeAllTo: string;
  playlists: ApiPlaylistModel[];
  playlistIds: string;
  isLoading: boolean;
  layout: "mobile" | "desktop";
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const history = useHistory();
  const { publicPlaylistLink } = usePageLinks(null);
  const displayed = playlists.slice(0, 8);

  useEffectOnce(() => {
    if (displayed.length > 0) {
      gaEventViewItemListPlaylist({
        playlists: displayed,
        listName: AnalyticsItemList.DISCOVERY_PLAYLISTS,
      });
    }
  }, [playlistIds]);

  function openPlaylist(playlist: ApiPlaylistModel) {
    // The recommended doc is the full detail shape — seed the
    // detail cache so the playlist page's header paints instantly.
    seedPlaylistDetailCache(playlist);
    gaEventSelectItemPlaylist({
      playlist,
      listName: AnalyticsItemList.DISCOVERY_PLAYLISTS,
    });
    history.push({
      pathname: publicPlaylistLink(playlist),
      state: { playlist },
    });
  }

  if (!isLoading && displayed.length === 0) return null;

  if (layout === "desktop")
    return (
      <DesktopCollectionsBand
        title={title}
        seeAllTo={seeAllTo}
        playlists={displayed}
        isLoading={isLoading}
        renderCard={(playlist) => (
          <SaveableCollectionCard
            playlist={playlist}
            listName={AnalyticsItemList.DISCOVERY_PLAYLISTS}
            onOpen={() => {
              openPlaylist(playlist);
            }}
          />
        )}
      />
    );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3 px-5">
        <SectionTitle title={title} />
        {displayed.length >= 2 ? (
          <SectionLink to={seeAllTo}>{t("See all")}</SectionLink>
        ) : null}
      </div>
      <div className="scrollbar-hidden flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-pl-5 px-5">
        {isLoading
          ? [0, 1].map((index) => (
              <div
                key={index}
                className="min-w-0 shrink-0 basis-[95%] snap-start"
              >
                <PlaylistCard loading variant="dark" />
              </div>
            ))
          : displayed.map((playlist) => (
              <div
                key={playlist.id}
                className="min-w-0 shrink-0 basis-[95%] snap-start"
              >
                <PlaylistCard
                  playlist={playlist}
                  variant="dark"
                  onClick={() => {
                    openPlaylist(playlist);
                  }}
                />
              </div>
            ))}
      </div>
    </div>
  );
}

/**
 * Counts the practice rail as SHOWN, and renders nothing.
 *
 * The half of the rail's funnel no server event can supply: the five
 * `practice_rail_*` events the API emits carry what the corpus gave each
 * request, and fire whether or not the response ever reached a screen. Reading
 * them as engagement is the trap this component exists to close.
 *
 * A component and not a `useTrackOnMount` in the page body for the same reason
 * as its sibling below: a hook runs unconditionally where it is called, and the
 * conditions a rail view depends on are decided in the render above.
 *
 * Keyed on the CORPUS and counted once per session, not once per mount. The
 * remount that used to re-count was the reader coming BACK from an article, so
 * the interaction being measured minted its own denominator event and the rate
 * could not exceed roughly 50% whatever readers did. Switching practice is a
 * genuinely different rail and still counts.
 */
export function PracticeRailViewed({
  corpus,
}: {
  corpus: PracticeRailCorpus;
}): null {
  useTrackOnMount(
    "practice-rail-viewed",
    { status: "shown", key: practiceRailCorpusKey(corpus) },
    ({ sourceEventId }) => {
      recordPracticeRailViewed(corpus, sourceEventId);
    }
  );
  return null;
}

/**
 * Counts the feed's recommended-room card as SHOWN, and renders nothing.
 *
 * A component rather than a `useTrackOnMount` call in the page body because the
 * card is conditional: a hook must run unconditionally where it is called, and
 * an impression must only be counted when the card genuinely reached the
 * screen. Mounting is that moment.
 *
 * This is the denominator half of the PRD's channel comparison — the GPT card
 * has had its impressions from the start, the feed card had none, so the two
 * conversion rates were not comparable at all.
 */
export function RoomRecommendationImpression({
  roomId,
  roomName,
}: {
  roomId: string;
  roomName: string;
}): null {
  useTrackOnMount(
    "room-recommendation-shown",
    // Keyed per PAGE, not per GA surface — see the twin on Explore.
    { status: "shown", key: `practice-feed:${roomId}` },
    () => {
      gaEventRoomRecommendationShown({ roomId, roomName, surface: "feed" });
    }
  );
  return null;
}
